import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from '@mui/material';
import {
  CONTACT_METHOD_TYPE,
  MIXPANEL_EVENTS,
  ProcureUser,
  Property,
  USER_ROLES,
} from 'vendor-marketplace-shared-library';
import Modal from '../common/Modal';
import React from 'react';
import { MuiTelInput } from 'mui-tel-input';
import { InviteDataInput } from '../../pages/users/UsersPage';
import { DropdownWithCheckboxes } from '../common/DropdownWithCheckboxes';
import PrimaryButton from '../common/styled-mui-overrides/PrimaryButton';
import useMixpanel from '../../hooks/useMixpanel';

type UserFormModalProps = {
  open: boolean;
  setOpen: (value: boolean) => void;
  onSubmit: () => void;
  inviteData: InviteDataInput;
  setInviteData: (value: InviteDataInput) => void;
  properties: Property[];
  resetForm: () => void;
  user?: ProcureUser;
};

export default function UserFormModal(props: UserFormModalProps) {
  const { mixpanelTrack } = useMixpanel();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  return (
    <Modal
      title={'Invite User'}
      open={props.open}
      onClose={() => {
        props.setOpen(false);
        props.resetForm();
      }}
      setOpen={props.setOpen}
    >
      <Grid container spacing={2} paddingY={2}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <TextField
              multiline
              name={'firstName'}
              label={'First name'}
              value={props.inviteData.firstName || ''}
              onChange={e =>
                props.setInviteData({ ...props.inviteData, firstName: e.target.value })
              }
            />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <TextField
              multiline
              name={'lastName'}
              label={'Last name'}
              value={props.inviteData.lastName || ''}
              onChange={e => props.setInviteData({ ...props.inviteData, lastName: e.target.value })}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Select a contact method</InputLabel>
            <Select
              value={props.inviteData.contactMethodType || ''}
              label={'Select a contact method'}
              onChange={e =>
                props.setInviteData({ ...props.inviteData, contactMethodType: e.target.value })
              }
            >
              <MenuItem value={CONTACT_METHOD_TYPE.PHONE}>Phone</MenuItem>
              {/*TODO: Allow for email contact method invites*/}
              {/*<MenuItem value={CONTACT_METHOD_TYPE.EMAIL}>Email</MenuItem>*/}
            </Select>
          </FormControl>
        </Grid>
        {props.inviteData.contactMethodType === CONTACT_METHOD_TYPE.PHONE ? (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <MuiTelInput
                fullWidth
                value={props.inviteData.contactMethodValue || ''}
                onChange={value =>
                  props.setInviteData({ ...props.inviteData, contactMethodValue: value })
                }
                placeholder='Phone Number'
                defaultCountry='US'
              />
            </FormControl>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <TextField
                multiline
                name={'email'}
                label={'Email'}
                value={props.inviteData.contactMethodValue || ''}
                onChange={e =>
                  props.setInviteData({ ...props.inviteData, contactMethodValue: e.target.value })
                }
              />
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12}>
          <FormControl>
            <FormLabel>Role</FormLabel>
            <RadioGroup
              value={props.inviteData.userRoleId}
              onChange={e => {
                props.setInviteData({ ...props.inviteData, userRoleId: Number(e.target.value) });
                mixpanelTrack(MIXPANEL_EVENTS.INVITATIONS_ROLES_MODAL_PRESSED);
              }}
              row
            >
              <FormControlLabel value={USER_ROLES.ADMIN.id} control={<Radio />} label='Admin' />
              <FormControlLabel value={USER_ROLES.MANAGER.id} control={<Radio />} label='Manager' />
              <FormControlLabel value={USER_ROLES.STAFF.id} control={<Radio />} label='Staff' />
            </RadioGroup>
          </FormControl>
        </Grid>
        {props.inviteData.userRoleId !== USER_ROLES.ADMIN.id && (
          <Grid item xs={12}>
            <PrimaryButton
              onClick={(e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)}
            >
              Select properties
            </PrimaryButton>
            <FormControl fullWidth>
              <DropdownWithCheckboxes
                entities={props.properties}
                onClose={() => setAnchorEl(null)}
                onSave={() => {}}
                anchorEl={anchorEl}
                selectedIds={props.inviteData.propertyIds || []}
                setSelectedIds={(selectedIds: string[]) =>
                  props.setInviteData({ ...props.inviteData, propertyIds: selectedIds })
                }
                displayFieldKey={'name'}
              />
            </FormControl>
          </Grid>
        )}
        <Grid item>
          <Button
            onClick={() => {
              props.onSubmit();
              mixpanelTrack(MIXPANEL_EVENTS.INVITATIONS_SENT);
            }}
          >
            Send invite
          </Button>
        </Grid>
      </Grid>
    </Modal>
  );
}
